import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSettings } from '../../../contexts/SettingsContext';

const RuntimeControls = () => {
    const { 
        settings,
        clientMaintenance, 
        availableClients, 
        updateSetting,
        updateClientMaintenance, 
        disableClientMaintenance,
        error
    } = useSettings();
    
    const [selectedClients, setSelectedClients] = useState(clientMaintenance);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingSettings, setUpdatingSettings] = useState({});

    const handleToggleChange = async (key, value) => {
        // Set loading state for this specific toggle
        setUpdatingSettings(prev => ({ ...prev, [key]: true }));
        
        try {
            if (key === 'maintenance_mode') {
                // If turning off maintenance mode, disable all client maintenance
                if (!value) {
                    try {
                        await disableClientMaintenance();
                        setSelectedClients([]);
                    } catch (error) {
                        console.error('Failed to disable client maintenance:', error);
                    }
                }
            }
            await updateSetting(key, value);
            console.log(`✅ ${key} updated successfully to: ${value}`);
        } catch (error) {
            console.error(`❌ Failed to update ${key}:`, error);
            // Error is already handled in the context
        } finally {
            // Clear loading state after a short delay
            setTimeout(() => {
                setUpdatingSettings(prev => ({ ...prev, [key]: false }));
            }, 500);
        }
    };

    const handleClientToggle = (clientId) => {
        // Only allow client selection if maintenance mode is enabled
        if (!settings.maintenance_mode) {
            return;
        }
        
        setSelectedClients(prev => {
            if (prev.includes(clientId)) {
                return prev.filter(id => id !== clientId);
            } else {
                return [...prev, clientId];
            }
        });
    };

    const handleApplyClientMaintenance = async () => {
        // Only allow client maintenance if maintenance mode is enabled
        if (!settings.maintenance_mode) {
            return;
        }
        
        setIsUpdating(true);
        try {
            if (selectedClients.length === 0) {
                await disableClientMaintenance();
            } else {
                await updateClientMaintenance(selectedClients);
            }
        } catch (error) {
            console.error('Failed to update client maintenance:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const isClientInMaintenance = (clientId) => {
        return clientMaintenance.includes(clientId);
    };

    // Sync selectedClients with clientMaintenance when component mounts or clientMaintenance changes
    useEffect(() => {
        setSelectedClients(clientMaintenance);
    }, [clientMaintenance]);

    const controls = [
        {
            key: 'debug_logging',
            label: 'Debug Logging',
            description: 'Enable detailed debug information for troubleshooting',
            icon: 'codicon:debug-all',
            helpText: 'Logs authentication events, configuration changes, and system operations to console output'
        },
        {
            key: 'api_logging',
            label: 'API Logging',
            description: 'Log all HTTP requests and responses',
            icon: 'material-symbols:api-rounded',
            helpText: 'Logs method, URL, status code, response time, and user context to console output'
        }
    ];

    return (
        <div className="flex flex-col gap-2">
            <h4 className="font-semibold text-secondary">Runtime Controls</h4>

            <hr className='border-border' />
            
            {/* Error Display */}
            {error && (
                <div className="mb-3 p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-red-300 text-sm font-medium">Configuration Error</span>
                    </div>
                    <p className="text-red-200 text-xs mt-1">{error}</p>
                </div>
            )}
            
            <div className="flex flex-col gap-3">
                {/* Maintenance Mode with Client Selection */}
                <div className="card-container flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-3">
                        <Icon 
                            icon="wpf:maintenance" 
                            className="text-2xl text-primary" 
                        />
                        <div className="flex flex-col items-start gap-1">
                            <span className="font-semibold">Maintenance Mode</span>
                            <p className="text-sm text-font-secondary">Block user access to the application</p>
                        </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer rounded-md"
                            checked={settings.maintenance_mode || false}
                            onChange={(e) => handleToggleChange('maintenance_mode', e.target.checked)}
                            disabled={updatingSettings.maintenance_mode}
                        />
                        <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-md peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-md after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                        {updatingSettings.maintenance_mode && (
                            <Icon icon="eos-icons:loading" className="absolute right-[-24px] text-primary animate-spin" />
                        )}
                    </label>
                </div>

                {/* Client Selection Dropdown - Only show when maintenance mode is enabled */}
                {settings.maintenance_mode && (
                    <>
                        <hr className="border-bgAccent" />
                        <div className="flex flex-col gap-3">
                            <div className="text-sm text-font-secondary">
                                Select which clients should be affected:
                            </div>
                            
                            {availableClients.length === 0 ? (
                                <div className="text-sm text-font-secondary text-center py-2">
                                    No clients available
                                </div>
                            ) : (
                                <div className="max-h-32 overflow-y-auto space-y-2">
                                    {availableClients.map((client) => (
                                        <div key={client.id} className="flex items-center justify-between p-2 rounded hover:bg-bgAccent transition-colors">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    id={`client-${client.id}`}
                                                    checked={selectedClients.includes(client.id)}
                                                    onChange={() => handleClientToggle(client.id)}
                                                    className="w-4 h-4 text-primary bg-bgSecondary border-bgAccent rounded focus:ring-primary"
                                                />
                                                <label 
                                                    htmlFor={`client-${client.id}`}
                                                    className="flex items-center gap-2 cursor-pointer"
                                                >
                                                    <span className="font-medium">{client.displayName || client.name}</span>
                                                    {isClientInMaintenance(client.id) && (
                                                        <Icon 
                                                            icon="mdi:maintenance" 
                                                            className="text-orange-500 text-sm" 
                                                            title="Currently in maintenance"
                                                        />
                                                    )}
                                                </label>
                                            </div>
                                            <div className="text-xs text-font-secondary">
                                                {client.category || 'general'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <div className="flex flex-row gap-2">
                                <button
                                    onClick={handleApplyClientMaintenance}
                                    disabled={isUpdating}
                                    className="flex-1 px-3 py-2 bg-primary text-white rounded text-sm hover:bg-primary/80 disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {isUpdating ? (
                                        <Icon icon="eos-icons:loading" className="animate-spin" />
                                    ) : (
                                        <Icon icon="material-symbols:save-rounded" />
                                    )}
                                    Apply to Selected Clients
                                </button>
                                
                                {clientMaintenance.length > 0 && (
                                    <button
                                        onClick={async () => {
                                            setSelectedClients([]);
                                            setIsUpdating(true);
                                            try {
                                                await disableClientMaintenance();
                                            } catch (error) {
                                                console.error('Failed to disable client maintenance:', error);
                                            } finally {
                                                setIsUpdating(false);
                                            }
                                        }}
                                        disabled={isUpdating}
                                        className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <Icon icon="mdi:close" />
                                        Disable All
                                    </button>
                                )}
                            </div>
                            
                            {clientMaintenance.length > 0 && (
                                <div className="text-xs text-orange-500 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                                    <Icon icon="mdi:information" className="inline mr-1" />
                                    {clientMaintenance.length} client(s) currently in maintenance mode
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="flex flex-row items-center gap-2">
                    {/* <Icon icon="mdi:file-document-outline" className="text-lg text-primary" /> */}
                    <h5 className="font-semibold text-secondary">Logging Controls</h5>
                </div>
                
                <hr className='border-border' />
                
                {controls.map((control, index) => (
                    <React.Fragment key={control.key}>
                        <div className="card-container flex flex-row items-center justify-between rounded-lg transition-colors">
                            <div className="flex flex-col items-start gap-2 flex-1">
                                <div className="flex flex-row items-center gap-3">
                                    <Icon 
                                        icon={control.icon} 
                                        className={`text-2xl ${settings[control.key] ? 'text-green-500' : 'text-secondary'}`} 
                                    />
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="font-semibold">{control.label}</span>
                                        <p className="text-sm text-font-secondary">{control.description}</p>
                                    </div>
                                </div>
                                <p className="text-font-secondary mt-1 flex items-center gap-1">
                                    <Icon icon="mdi:information-outline" className="text-base" />
                                    <span className="text-xs flex-1">{control.helpText}</span>
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={settings[control.key] || false}
                                    onChange={(e) => handleToggleChange(control.key, e.target.checked)}
                                    disabled={updatingSettings[control.key]}
                                />
                                <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-md peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-md after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                                {updatingSettings[control.key] && (
                                    <Icon icon="eos-icons:loading" className="absolute right-[-24px] text-primary animate-spin" />
                                )}
                            </label>
                        </div>
                    </React.Fragment>
                ))}
                
                {/* Logging Status Summary */}
                <div className="flex items-center gap-1 p-3 rounded-md bg-blue-400 bg-opacity-20 border border-blue-500 transition-colors">
                    <Icon icon="typcn:info" className="text-xl text-blue-500" />
                    <p className="text-sm text-font-primary font-semibold flex-1">
                        All logs appear in the server console output where the backend is running
                    </p>                    
                </div>
            </div>
        </div>
    );
};

export default RuntimeControls;
